/**
 * NAT-2481 — what the position exports (receivables, payables, slow movers) share: a sheet built
 * from fields that each declare whether the response actually carried them, and the aging
 * tables' preamble that says what the screen was showing.
 */
import { type XlsxCellKind, type XlsxSheetSpec, type XlsxValue } from 'src/utils/file-downloader';
import { exportAsOfDay, exportTimestamp } from 'src/utils/get-date-worlds-format';

export type ExportT = (key: string, opts?: Record<string, unknown>) => string;

/** One column of an export: its header and kind, the cell of a row, and whether to keep it. */
export type ExportField<R> = {
  header: string;
  kind: XlsxCellKind;
  cell: (row: R) => XlsxValue;
  /**
   * False when no row carries the field (a backend that predates it): the column is dropped
   * rather than written empty, which would read as "no value" for every row.
   */
  carried?: boolean;
};

export function sheetOf<R>(name: string, fields: ExportField<R>[], rows: R[]): XlsxSheetSpec {
  const kept = fields.filter((f) => f.carried !== false);
  return {
    name,
    columns: kept.map(({ header, kind }) => ({ header, kind })),
    rows: rows.map((row) => kept.map((f) => f.cell(row))),
  };
}

/** True when at least one row sent the field at all — null counts as sent, undefined does not. */
export const anyCarries = <R>(rows: R[], value: (row: R) => unknown) =>
  rows.some((row) => value(row) !== undefined);

/**
 * The aging tables have no period: they are a position as of a day. That day is the response's
 * own when it states one (`asOf`, a yyyy-MM-dd Rome day), else the day of the export.
 */
export function agingPreamble({
  page,
  asOf,
  bucketLabel,
  agentLabel,
  sortLabel,
  t,
  now,
}: {
  page: string;
  asOf?: string | null;
  bucketLabel: string | null;
  /** NAT-2795 — the admin's agent filter in words; omitted (no line) where there is none. */
  agentLabel?: string | null;
  sortLabel: string;
  t: ExportT;
  now: Date;
}): [string, string][] {
  return [
    [t('export_pagina'), page],
    [t('export_situazione_al'), exportAsOfDay(asOf, now)],
    [t('export_fascia'), bucketLabel ?? t('export_tutte_le_fasce')],
    ...(agentLabel ? [[t('export_agente'), agentLabel] as [string, string]] : []),
    [t('export_ordinamento'), sortLabel],
    [t('export_esportato_il'), exportTimestamp(now)],
  ];
}
